import { motion, AnimatePresence } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlinePlus } from 'react-icons/ai';
import { BiX } from 'react-icons/bi';
import { FaWandMagicSparkles } from "react-icons/fa6";
import { ImSpinner8 } from 'react-icons/im';

import { Priority, ChecklistItem } from '@/apps/Checklist/stores/useChecklistStore';
import { generateSubtasks } from '@/common/ai/aiClient';
import handleException from '@/common/utils/errors';

interface TaskItemProps {
  id: string;
  content: string;
  completed: boolean;
  priority: Priority;
  subitems?: ChecklistItem[];
  onToggle: (id: string, completed: boolean) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, content: string) => void;
  onUpdatePriority: (id: string, priority: Priority) => void;
  onAddSubitem?: (parentId: string, content: string) => void;
  isParent?: boolean;
}

export default function TaskItem({
  id,
  content,
  completed,
  priority,
  subitems,
  onToggle,
  onDelete,
  onUpdate,
  onUpdatePriority,
  onAddSubitem,
  isParent
}: TaskItemProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = React.useState(false);
  const [editContent, setEditContent] = React.useState(content);
  const [isAddingSubitem, setIsAddingSubitem] = React.useState(false);
  const [subitemContent, setSubitemContent] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isGeneratingSubtasks, setIsGeneratingSubtasks] = React.useState(false);

  const hasSubitems = subitems && subitems.length > 0;
  // Check if this is a parent task (not a subtask itself)

  const handleSave = () => {
    if (!editContent.trim()) return;
    onUpdate(id, editContent);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent(content);
  };

  const handleAddSubitem = () => {
    if (!subitemContent.trim() || !onAddSubitem) return;
    onAddSubitem(id, subitemContent);
    setSubitemContent('');
    setIsAddingSubitem(false);
    setIsExpanded(true); // Auto-expand when adding a new subitem
  };

  // Handle AI-generated subtasks
  const handleGenerateSubtasks = async () => {
    if (!onAddSubitem) return;

    try {
      setIsGeneratingSubtasks(true);
      // Generate up to 4 subtasks using AI
      const response = await generateSubtasks(content, 4);

      if (response.error) {
        throw new Error(response.error);
      }

      if (response.data && Array.isArray(response.data)) {
        // Add each subtask
        for (const subtask of response.data) {
          onAddSubitem(id, subtask);
        }

        // Expand to show the new subtasks
        setIsExpanded(true);
      }
    } catch (error) {
      handleException(error);
    } finally {
      setIsGeneratingSubtasks(false);
    }
  };

  const getPriorityColor = (p: Priority) => {
    switch (p) {
      case 2: // high
        return {
          base: 'bg-rose-400',
          hover: 'hover:bg-rose-500',
          ring: 'ring-rose-400'
        };
      case 1: // medium
        return {
          base: 'bg-amber-400',
          hover: 'hover:bg-amber-500',
          ring: 'ring-amber-400'
        };
      case 0: // low
        return {
          base: 'bg-emerald-400',
          hover: 'hover:bg-emerald-500',
          ring: 'ring-emerald-400'
        };
    }
  };

  const PrioritySelector = () => (
    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
      {[0, 1, 2].map((p) => {
        const colors = getPriorityColor(p as Priority);
        return (
          <div key={p} className="relative group/priority">
            <button
              className={`w-5 h-5 rounded-full transition-all duration-200 
                ${colors.base} ${colors.hover}
                ${priority === p ? `scale-110 ring-2 ring-offset-2 ${colors.ring}` : ''}`}
              onClick={() => onUpdatePriority(id, p as Priority)}
            />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover/priority:block">
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                {p === 0 ? t('checklist.priorityLow') : p === 1 ? t('checklist.priorityMedium') : t('checklist.priorityHigh')} {t('checklist.priority')}
              </div>
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
            </div>
          </div>
        );
      })}
    </div>
  );

  const CurrentPriority = () => {
    const colors = getPriorityColor(priority);
    return (
      <div className="relative group/current">
        <div
          className={`w-3 h-3 rounded-full ${colors.base} opacity-100 group-hover:opacity-0 transition-opacity`}
        />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover/current:block">
          <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {priority === 0 ? t('checklist.priorityLow') : priority === 1 ? t('checklist.priorityMedium') : t('checklist.priorityHigh')} {t('checklist.priority')}
          </div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div
        className={`flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border 
          ${hasSubitems ? 'border-gray-200' : 'border-gray-100 hover:border-gray-200'}
          ${hasSubitems && isExpanded ? 'border-b-0 rounded-b-none' : ''}
          transition-all duration-300 group`}
        style={{
          transform: 'translateZ(0)', // Forces GPU acceleration
        }}
      >
        <input
          checked={completed}
          className="h-5 w-5 rounded-full border-2 border-gray-300 text-[#FFD66B] focus:ring-[#FFD66B]"
          type="checkbox"
          onChange={(e) => onToggle(id, e.target.checked)}
        />
        {isEditing ? (
          <div className="flex-1 flex gap-2">
            <input
              className="flex-1 px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD66B]"
              value={editContent}
              autoFocus
              onChange={(e) => setEditContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <button
              className="text-green-600 hover:text-green-700 transition-colors px-2 text-sm font-medium"
              onClick={handleSave}
            >
              {t('common.save')}
            </button>
            <button
              className="text-gray-500 hover:text-gray-700 transition-colors px-2 text-sm font-medium"
              onClick={handleCancel}
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 flex items-center">
              {hasSubitems && (
                <button
                  aria-expanded={isExpanded}
                  className={`mr-2 text-gray-400 hover:text-gray-600 transition-colors p-1
                    ${hasSubitems ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title={isExpanded ? t('checklist.collapseSubtasks') : t('checklist.expandSubtasks')}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                  </svg>
                </button>
              )}
              <span
                className={`${completed ? 'line-through text-gray-400' : 'text-gray-700'}`}
                onDoubleClick={() => setIsEditing(true)}
              >
                {content}
              </span>
              {hasSubitems && (
                <span className="ml-2 text-xs text-gray-400 font-medium">
                  ({subitems!.length} {subitems!.length === 1 ? t('checklist.subtask') : t('checklist.subtasks')})
                </span>
              )}
            </div>

            <div className="relative flex items-center">
              <CurrentPriority />
              <div className="absolute right-0">
                <PrioritySelector />
              </div>
            </div>

            {/* AI Button - Only show for parent tasks */}
            {isParent && (
              <button
                className="text-gray-400 hover:text-purple-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
                disabled={isGeneratingSubtasks}
                title={t('checklist.generateWithAI')}
                onClick={handleGenerateSubtasks}
              >
                {isGeneratingSubtasks ? (
                  <ImSpinner8 className="w-4 h-4 animate-spin" />
                ) : (
                  <FaWandMagicSparkles className="w-4 h-4" />
                )}
              </button>
            )}

            {onAddSubitem && (
              <button
                className="text-gray-400 hover:text-blue-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
                title={t('checklist.addSubtask')}
                onClick={() => setIsAddingSubitem(true)}
              >
                <AiOutlinePlus className="w-4 h-4" />
              </button>
            )}

            <button
              className="text-gray-400 hover:text-red-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
              onClick={() => onDelete(id)}
            >
              <BiX className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {isAddingSubitem && (
        <div className="ml-8 flex gap-2 mt-2 bg-gray-50 p-3 rounded-lg">
          <input
            className="flex-1 px-3 py-1 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD66B]"
            placeholder={t('checklist.addSubtaskPlaceholder')}
            value={subitemContent}
            autoFocus
            onChange={(e) => setSubitemContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddSubitem();
              if (e.key === 'Escape') setIsAddingSubitem(false);
            }}
          />
          <button
            className="bg-gray-900 text-white px-3 py-1 rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm"
            onClick={handleAddSubitem}
          >
            {t('common.add')}
          </button>
          <button
            className="text-gray-500 hover:text-gray-700 transition-colors px-2 text-sm"
            onClick={() => setIsAddingSubitem(false)}
          >
            {t('common.cancel')}
          </button>
        </div>
      )}

      <AnimatePresence>
        {hasSubitems && isExpanded && (
          <motion.div
            animate={{ opacity: 1, height: 'auto' }}
            className="ml-8 space-y-2 border border-gray-200 border-t-0 rounded-b-xl bg-gray-50 p-3"
            exit={{ opacity: 0, height: 0 }}
            initial={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {subitems!.map((subitem) => (
              <TaskItem
                key={subitem.id}
                completed={subitem.completed}
                content={subitem.content}
                id={subitem.id}
                isParent={subitem.parent_id === null}
                priority={subitem.priority}
                onAddSubitem={onAddSubitem}
                onDelete={onDelete}
                onToggle={onToggle}
                onUpdate={onUpdate}
                onUpdatePriority={onUpdatePriority}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
